import { BookOpen, Pencil, Trash2, X, Check } from "lucide-react";
import { useState, useCallback } from "react";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";

interface AgentMemory {
  id: string;
  content: string;
  category: string;
  created_at: string;
  updated_at: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  general: "bg-gray-100 text-gray-700",
  preference: "bg-blue-100 text-blue-700",
  decision: "bg-purple-100 text-purple-700",
  context: "bg-warning-100 text-warning-700",
  action: "bg-success-100 text-success-700",
  correction: "bg-red-100 text-red-700",
};

export const AgentMemories = ({
  memories,
  onChange,
  isReadOnly,
}: {
  memories: AgentMemory[];
  onChange: (memories: AgentMemory[]) => void;
  isReadOnly?: boolean;
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");

  const handleStartEdit = useCallback((memory: AgentMemory) => {
    setEditingId(memory.id);
    setEditContent(memory.content);
  }, []);

  const handleSaveEdit = useCallback(
    (memoryId: string) => {
      const updated = memories.map((m) =>
        m.id === memoryId
          ? {
              ...m,
              content: editContent,
              updated_at: new Date().toISOString(),
            }
          : m
      );
      onChange(updated);
      setEditingId(null);
      setEditContent("");
    },
    [memories, editContent, onChange]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditContent("");
  }, []);

  const handleDelete = useCallback(
    (memoryId: string) => {
      onChange(memories.filter((m) => m.id !== memoryId));
    },
    [memories, onChange]
  );

  if (!memories || memories.length === 0) {
    return (
      <NoxusEmptyBlock
        size="sm"
        icon={BookOpen}
        title="No memories stored yet"
        description="The agent will build memories as it converses with users"
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {memories.map((memory) => (
        <div
          key={memory.id}
          className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:shadow-sm transition-shadow"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                  CATEGORY_COLORS[memory.category] || CATEGORY_COLORS.general
                }`}
              >
                {memory.category}
              </span>
              <span className="text-[10px] text-muted-foreground font-mono">
                {memory.id.slice(0, 8)}
              </span>
            </div>
            {editingId === memory.id ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="flex-1 text-sm border rounded px-2 py-1 bg-background"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveEdit(memory.id);
                    if (e.key === "Escape") handleCancelEdit();
                  }}
                />
                <button
                  onClick={() => handleSaveEdit(memory.id)}
                  className="p-1 rounded hover:bg-success-100 text-success-600"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="p-1 rounded hover:bg-gray-100 text-gray-500"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <p className="text-sm text-foreground">{memory.content}</p>
            )}
          </div>
          {!isReadOnly && editingId !== memory.id && (
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => handleStartEdit(memory)}
                className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                title="Edit memory"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleDelete(memory.id)}
                className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600"
                title="Delete memory"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
